import React, { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { get, isNil } from 'lodash';
import { useNavigate } from 'react-router-dom';
import GrowingBarChartSVG from 'src/assets/svg/growing-bar-chart.svg';
import { AlertModal, Table } from 'src/componentsTailwind/index';
import { TYPES } from 'src/componentsTailwind/Table/Table';
import { useCompany } from 'src/queries/company';
import { useTree } from 'src/queries/tree';
import { appUtils, Base } from 'src/components/';
import CircleV2 from 'src/components/Circle/CircleV2';
import CircledIcon from 'src/components/CircledIcon/CircledIcon';
import FeedbackFlyout from 'src/containers/UserProfile/FeedbackDashboard/components/FeedbackFlyout';
import COMMON_CONSTANTS from 'common/commonConstants';
import commonDateUtils from 'common/commonDateUtils';
import commonReviewUtils from 'common/commonReviewUtils';
import commonTreeUtils from 'common/commonTreeUtils';
import commonUtils from 'common/commonUtils';
import STYLE from 'src/constants/style';

const { ANONYMOUS_REVIEW, FEEDBACK_TYPE, USER_STATE } = COMMON_CONSTANTS;

const HistoricReviews = ({
  reviews,
  isLoading,
  userId,
  showViewReviewButton,
  showRequestReview = true,
  pagination: { totalPages, currentPage, selectPage } = {},
  queryOptions = {},
  onAction,
  actionText,
  actionIcon = null,
  showActionRule = () => true
}) => {
  const navigate = useNavigate();
  const loggedUserId = appUtils.getLoggedUserId();

  const {
    data: company,
    isFetching: isFetchingCompany,
    isError: isErrorCompany
  } = useCompany(queryOptions);
  const {
    data: { tree, deleted },
    isFetching: isFetchingTree,
    isError: isErrorTree
  } = useTree(queryOptions);
  const { showRequestFeedbackButton, populateRevieweeOnRequestFeedback } = useFlags();

  const isFetching = isFetchingCompany || isFetchingTree;
  const isError = isErrorCompany || isErrorTree;
  const isReady = company && company.id && tree && tree.id && !isFetching && !isError;

  const [isFeedbackFlyoutOpen, setIsFeedbackFlyoutOpen] = useState(false);
  const [flyoutFeedback, setFlyoutFeedback] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserAccount, setSelectedUserAccount] = useState(null);

  if (!isReady) {
    return null;
  }

  const handleShowReview = (reviewId, feedbackType) => {
    if (feedbackType === FEEDBACK_TYPE.GENERAL_FEEDBACK) {
      return navigate(`/dashboard/feedback/view/${reviewId}`);
    }
    navigate(`/dashboard/reviews/view/${reviewId}`);
  };

  const columns = [
    {
      label: 'Date',
      span: 0.5,
      tooltip: false
    },
    {
      label: 'Reviewer',
      span: 0.75,
      tooltip: true
    },
    {
      label: 'Reviewee',
      tooltip: true,
      span: 0.75
    },
    {
      label: 'Answer',
      tooltip: true
    },
    {
      label: 'Comments',
      tooltip: false,
      span: 2
    },
    {
      label: 'View',
      type: TYPES.ACTION,
      span: 0.3,
      tooltip: false
    }
  ];

  if (actionText) {
    columns.push({
      label: actionText,
      type: TYPES.ACTION,
      hidden: true,
      span: actionText ? 0.5 : 0,
      tooltip: false
    });
  }

  const userTree = tree && tree.id && userId
    ? commonTreeUtils.findNodeById(tree, userId)
    : null;

  const openFeedbackFlyout = (reviewId) => {
    setFlyoutFeedback(() => {
      const review = reviews.find(
        (f) => f._id === reviewId || f.id === reviewId
      );

      if (review) {
        setIsFeedbackFlyoutOpen(true);
        return review;
      }
    });
  };

  const rows = reviews.map((review) => {
    const {
      id,
      reviewedDate,
      answer,
      text,
      reviewerName,
      reviewerImage,
      revieweeName: explicitRevieweeName,
      revieweeImage: explicitRevieweeImage,
      reviewer: reviewerId,
      reviewee: revieweeId
    } = review || {};

    const reviewer = commonTreeUtils.findNodeById(tree, reviewerId)
      || deleted.find((d) => d.id === reviewerId);
    const reviewee = commonTreeUtils.findNodeById(tree, revieweeId)
      || deleted.find((d) => d.id === revieweeId);

    const revieweeName = explicitRevieweeName ?? reviewee?.name ?? '';
    const revieweeImage = explicitRevieweeImage ?? reviewee?.imageUrl ?? '';

    const formattedDate = commonDateUtils.dateToMonthDayFormat(
      new Date(reviewedDate),
      ''
    );

    const isReviewerArchived = reviewer?.active === USER_STATE.UNASSIGNED;
    const isRevieweeArchived = reviewee?.active === USER_STATE.UNASSIGNED;

    const formattedAnswer = isNil(answer)
      ? 'Free response'
      : commonReviewUtils.getReviewAnswerLabel(review, {
        useQuestionSkippedLabel: true
      });

    const viewAction = () => openFeedbackFlyout(id);

    const isReviewerAnonymous = reviewerName === ANONYMOUS_REVIEW.NAME;
    const isRevieweeAnonymous = revieweeName === ANONYMOUS_REVIEW.NAME;

    let reviewerProfileUrl = null;
    if (!isReviewerAnonymous) {
      reviewerProfileUrl = loggedUserId === reviewerId
        ? `/dashboard/me/participation`
        : `/dashboard/profile/${reviewer.id}/participation`;
    }

    let revieweeProfileUrl = null;
    if (!isRevieweeAnonymous) {
      revieweeProfileUrl = loggedUserId === revieweeId
        ? `/dashboard/me/participation`
        : `/dashboard/profile/${reviewee.id}/participation`;
    }

    const { firstName: reviewerFirstName, lastName: reviewerLastName } = commonUtils.getFirstAndLastName(reviewerName);

    const { firstName: revieweeFirstName, lastName: revieweeLastName } = commonUtils.getFirstAndLastName(revieweeName);

    const row = [
      { label: formattedDate },
      {
        label: commonUtils.getFullName(
          reviewerFirstName,
          reviewerLastName,
          isReviewerArchived ? '' : 'lastName'
        ),
        image: reviewerImage,
        type: TYPES.IMAGE_WITH_TEXT,
        classes: `${isReviewerAnonymous ? 'cursor-default' : ''}`,
        onClick: () => {
          if (!isReviewerAnonymous) {
            setSelectedUserAccount({
              id: reviewer.id,
              name: reviewerName,
              firstName: reviewerName.split(' ')[0],
              imageUrl: reviewerImage,
              profileUrl: reviewerProfileUrl
            });
            setIsModalOpen(true);
          }
        }
      },
      {
        label: commonUtils.getFullName(
          revieweeFirstName,
          revieweeLastName,
          isRevieweeArchived ? '' : 'lastName'
        ),
        image: revieweeImage,
        type: TYPES.IMAGE_WITH_TEXT,
        classes: `${isRevieweeAnonymous ? 'cursor-default' : ''}`,
        onClick: () => {
          if (!isRevieweeAnonymous) {
            setSelectedUserAccount({
              id: reviewee.id,
              name: revieweeName,
              firstName: revieweeName.split(' ')[0],
              imageUrl: revieweeImage,
              profileUrl: revieweeProfileUrl
            });
            setIsModalOpen(true);
          }
        }
      },
      { label: formattedAnswer },
      {
        label: text,
        id: `${id}-review-comments`,
        type: TYPES.HTML
      },
      {
        label: 'View',
        type: TYPES.ACTION,
        onClick: viewAction
      }
    ];

    if (actionText && showActionRule(review)) {
      row.push({
        label: actionText,
        icon: actionIcon(),
        classes: 'text-success-green flex align-middle items-center gap-1',
        type: TYPES.ACTION,
        onClick: () => {
          if (onAction) {
            onAction(review.id, review.feedbackType);
          }
        }
      });
    }

    return row;
  });

  const handleRequestReview = () => {
    const baseUrl = '/dashboard/request-feedback/review';
    const url = populateRevieweeOnRequestFeedback
      ? `${baseUrl}?revieweeId=${userId}`
      : baseUrl;

    return navigate(url);
  };

  const pagination = {
    currentPage,
    setCurrentPage: selectPage,
    totalPages
  };

  return (
    <>
      <AlertModal
        isOpen={isModalOpen}
        close={() => setIsModalOpen(false)}
        onAction={() => {
          window.open(selectedUserAccount.profileUrl, '_blank');
          setIsModalOpen(false);
          setSelectedUserAccount(null);
        }}
        title={get(selectedUserAccount, 'name', 'View More Details')}
        content={`View ${get(selectedUserAccount, 'firstName', 'this user')}'s dashboard see more details.`}
        actionText='View Dashboard'
        isLoading={isFetching}
        type='success'
        customIcon={(
          <CircleV2
            imageUrl={get(selectedUserAccount, 'imageUrl', null)}
            size='sm'
          />
        )}
      />
      <FeedbackFlyout
        width={40}
        feedback={flyoutFeedback}
        isOpen={isFeedbackFlyoutOpen}
        setIsOpen={setIsFeedbackFlyoutOpen}
        showXButton={false}
      />
      <Base
        classes={`${STYLE.CONTAINER_WHITE} !p-4`}
        relative
        loading={isLoading}
      >
        <CircledIcon svg={<GrowingBarChartSVG className='text-purple' />} />
        <p className='text-xl text-black font-bold mb-0 mr-8'>
          Historic Feedback
        </p>
        <p className='text-md font-normal my-2 leading-5 text-bluish-gray mb-6'>
          List of previously submitted feedback responses.
          {userTree ? ` Only visible to ${userTree.name}’s managers.` : ''}
        </p>
        <Table
          headRowClasses='!px-0 [&>*:first-child]:!pl-4 [&>*:last-child]:!pr-0'
          rowClasses='!px-0 [&>*:first-child]:!pl-4 [&>*:last-child]:!pr-0 !h-[102px]'
          headClasses='[&_*]:!text-sm [&_*]:!truncate [&>*:first-child]:!pl-0 [&>*:last-child]:!pr-0'
          columns={columns}
          rows={rows}
          pagination={pagination}
          placeholderMessage='No feedback responses'
        />
      </Base>
    </>
  );
};

export default HistoricReviews;
